import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetBody } from './ui/Sheet';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Alert } from './ui/Alert';
import { SkeletonRows } from './ui/Skeleton';
import { apiFetch } from '../lib/api';
import { formatDateTime, formatNumber, formatPercent } from '../lib/format';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import { StepUpCodeField } from './StepUpCodeField';
import { ApiError } from '../types/api';
import type { HealthSummary, WarmupAdmin } from '../types/api';

function warmupErrorText(err: unknown, fallback: string): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401) return 'The code is incorrect.';
  if (err instanceof ApiError && err.status === 403)
    return 'Only an administrator can change warm-up.';
  if (err instanceof ApiError && err.status === 409 && err.body.message) return err.body.message;
  return fallback;
}

/**
 * Plan (grey) vs. actual reached step (accent) — same bar pattern as
 * `HealthRail`'s `WarmupCard`, but here per sender and with the whole
 * plan, not just the next few days.
 */
function CurveChart({ plan, dayOffset }: { plan: WarmupAdmin['plan']; dayOffset: number }) {
  if (plan.length === 0) return null;
  const max = Math.max(...plan.map((s) => s.maxSends));
  return (
    <div
      className="flex gap-[3px] items-end h-16"
      role="img"
      aria-label={`Warm-up curve, day ${dayOffset}, ${plan.length} steps in the plan`}
    >
      {plan.map((s) => (
        <div
          key={s.dayOffset}
          className={
            s.dayOffset <= dayOffset
              ? 'flex-grow bg-accent rounded-sm'
              : 'flex-grow bg-[#D4D5F7] rounded-sm'
          }
          style={{
            height: `${Math.max(6, (s.maxSends / max) * 100)}%`,
            outline: s.dayOffset === dayOffset ? '2px solid #18181B' : undefined,
            outlineOffset: s.dayOffset === dayOffset ? '1px' : undefined,
          }}
          title={`Day ${s.dayOffset}: at most ${formatNumber(s.maxSends)}`}
        />
      ))}
    </div>
  );
}

/** Highest daily limit the API accepts: the SES account quota (packages/core `MAX_SENDER_DAILY_LIMIT`). */
const MAX_DAILY_LIMIT = 50_000;

function StartWarmupForm({
  senderId,
  currentDailyLimit,
  restart = false,
  onDone,
}: {
  senderId: string;
  currentDailyLimit: number;
  /** True when a plan is already running: the same request restarts it at day 0. */
  restart?: boolean;
  onDone: () => void;
}) {
  const [target, setTarget] = useState('');
  const [startLimit, setStartLimit] = useState('');
  const [dailyLimit, setDailyLimit] = useState(String(currentDailyLimit));
  const [totpCode, setTotpCode] = useState('');
  const queryClient = useQueryClient();
  const stepUp = useStepUpWindow();
  const codeId = useId();
  const limitId = useId();
  const limitHintId = useId();

  const limitValue = Number(dailyLimit.trim());
  const limitValid =
    dailyLimit.trim() !== '' &&
    Number.isInteger(limitValue) &&
    limitValue >= 1 &&
    limitValue <= MAX_DAILY_LIMIT;

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<WarmupAdmin>(`/senders/${encodeURIComponent(senderId)}/warmup/start`, {
        method: 'POST',
        body: {
          ...(stepUp.active ? {} : stepUpBody(totpCode)),
          ...(target.trim() ? { target: Number(target.trim()) } : {}),
          ...(startLimit.trim() ? { startLimit: Number(startLimit.trim()) } : {}),
          ...(limitValue !== currentDailyLimit ? { dailyLimit: limitValue } : {}),
        },
      }),
    onSuccess: () => {
      stepUp.onStepUpSuccess();
      setTotpCode('');
      setTarget('');
      setStartLimit('');
      void queryClient.invalidateQueries({ queryKey: ['warmup', senderId] });
      onDone();
    },
    onError: stepUp.onStepUpError,
  });

  const canConfirm =
    (stepUp.active || stepUpCodeReady(totpCode)) && limitValid && !mutation.isPending;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (canConfirm) mutation.mutate();
      }}
    >
      {mutation.isError ? (
        <Alert>{warmupErrorText(mutation.error, 'Warm-up could not be started. Try again.')}</Alert>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={limitId}>Daily limit (emails/day)</Label>
        <Input
          id={limitId}
          type="number"
          min={1}
          max={MAX_DAILY_LIMIT}
          value={dailyLimit}
          onChange={(e) => setDailyLimit(e.target.value)}
          aria-describedby={limitHintId}
          aria-invalid={!limitValid}
          required
        />
        <div id={limitHintId} className="text-xs text-muted">
          The most this sender may ever send in a day. Warm-up climbs toward it but never above. At
          most {formatNumber(MAX_DAILY_LIMIT)} (the Amazon SES quota).
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="warmup-target">Target (emails/day, optional)</Label>
        <Input
          id="warmup-target"
          type="number"
          min={1}
          placeholder="The entire eligible list"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
        />
        <div className="text-xs text-muted">
          Empty = the entire eligible list, but never above the sender's daily limit.
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="warmup-start-limit">First-day limit (emails/day, optional)</Label>
        <Input
          id="warmup-start-limit"
          type="number"
          min={1}
          max={MAX_DAILY_LIMIT}
          placeholder="200"
          value={startLimit}
          onChange={(e) => setStartLimit(e.target.value)}
        />
        <div className="text-xs text-muted">
          Where the curve starts. Empty = 200. It grows 1.5× every second day while bounces and
          complaints stay low.
        </div>
      </div>
      <StepUpCodeField
        id={codeId}
        value={totpCode}
        onChange={setTotpCode}
        active={stepUp.active}
        minutesLeft={stepUp.minutesLeft}
        hint={
          stepUp.active
            ? undefined
            : restart
              ? 'Restarting begins the curve again at day 0 and requires a 2FA code.'
              : 'Starting requires a 2FA code, just like other changes that increase the limit.'
        }
      />
      <Button type="submit" variant="primary" disabled={!canConfirm} className="h-11 self-end">
        {mutation.isPending
          ? restart
            ? 'Restarting…'
            : 'Starting…'
          : restart
            ? 'Restart warm-up'
            : 'Start warm-up'}
      </Button>
    </form>
  );
}

function ActiveWarmupPanel({
  senderId,
  warmup,
  health,
  isAdmin,
}: {
  senderId: string;
  warmup: WarmupAdmin;
  health: HealthSummary | undefined;
  isAdmin: boolean;
}) {
  const [restarting, setRestarting] = useState(false);
  const queryClient = useQueryClient();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['warmup', senderId] });

  const pauseMutation = useMutation({
    mutationFn: () =>
      apiFetch<WarmupAdmin>(`/senders/${encodeURIComponent(senderId)}/warmup/pause`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: invalidate,
  });
  const resetMutation = useMutation({
    mutationFn: () =>
      apiFetch<WarmupAdmin>(`/senders/${encodeURIComponent(senderId)}/warmup/reset`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: invalidate,
  });

  return (
    <div className="flex flex-col gap-4">
      {pauseMutation.isError ? (
        <Alert>{warmupErrorText(pauseMutation.error, 'Could not pause warm-up.')}</Alert>
      ) : null}
      {resetMutation.isError ? (
        <Alert>{warmupErrorText(resetMutation.error, 'Could not reset warm-up.')}</Alert>
      ) : null}
      <div className="flex flex-col gap-1 text-sm">
        <div className="flex">
          <span className="text-muted">Status</span>
          <span className="ml-auto font-medium">{warmup.paused ? 'Paused' : 'Running'}</span>
        </div>
        <div className="flex">
          <span className="text-muted">Target</span>
          <span className="ml-auto font-mono">
            {warmup.target !== null ? formatNumber(warmup.target) : '—'}
          </span>
        </div>
        <div className="flex">
          <span className="text-muted">Daily limit</span>
          <span className="ml-auto font-mono">{formatNumber(warmup.dailyLimit)}</span>
        </div>
        <div className="flex">
          <span className="text-muted">Today at most</span>
          <span className="ml-auto font-mono">{formatNumber(warmup.limitToday)}</span>
        </div>
        <div className="flex">
          <span className="text-muted">Started</span>
          <span className="ml-auto">
            {warmup.startedAt !== null ? formatDateTime(warmup.startedAt) : '—'}
          </span>
        </div>
      </div>
      <CurveChart plan={warmup.plan} dayOffset={warmup.dayOffset} />
      <div className="text-xs text-muted">
        Plan vs. actual reached step. The curve (×1.5 every 2nd day) is a plan, not a guarantee — it
        is only kept as long as bounce and complaints stay under the limits.
      </div>
      {health ? (
        <div className="flex flex-col gap-1 text-sm">
          <div className="flex">
            <span className="text-muted">
              Bounce ({health.bounce.window === '7d' ? '7 days' : health.bounce.window})
            </span>
            <span className="ml-auto font-mono">{formatPercent(health.bounce.rate)}</span>
          </div>
          <div className="flex">
            <span className="text-muted">
              Complaints ({health.complaint.window === '7d' ? '7 days' : health.complaint.window})
            </span>
            <span className="ml-auto font-mono">{formatPercent(health.complaint.rate)}</span>
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        {!warmup.paused ? (
          <Button
            variant="secondary"
            disabled={pauseMutation.isPending}
            onClick={() => pauseMutation.mutate()}
          >
            {pauseMutation.isPending ? 'Pausing…' : 'Pause'}
          </Button>
        ) : null}
        <Button
          variant="secondary"
          disabled={resetMutation.isPending}
          onClick={() => resetMutation.mutate()}
        >
          {resetMutation.isPending ? 'Resetting…' : 'Reset to day 0'}
        </Button>
        {isAdmin && !restarting ? (
          <Button variant="secondary" onClick={() => setRestarting(true)}>
            Change limit or target
          </Button>
        ) : null}
      </div>
      {restarting ? (
        <StartWarmupForm
          senderId={senderId}
          currentDailyLimit={warmup.dailyLimit}
          restart
          onDone={() => setRestarting(false)}
        />
      ) : null}
    </div>
  );
}

function WarmupSheetBody({
  senderId,
  isAdmin,
  onClose,
}: {
  senderId: string;
  isAdmin: boolean;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['warmup', senderId],
    queryFn: () => apiFetch<WarmupAdmin>(`/senders/${encodeURIComponent(senderId)}/warmup`),
  });
  const { data: health } = useQuery({
    queryKey: ['health-summary'],
    queryFn: () => apiFetch<HealthSummary>('/health-summary'),
  });

  return (
    <SheetBody>
      {isLoading ? (
        <SkeletonRows label="Loading warm-up status…" rows={3} rowClassName="h-16" />
      ) : null}
      {isError ? <Alert>The warm-up status could not be loaded.</Alert> : null}
      {data && !data.active ? (
        <>
          <p className="m-0 text-sm text-muted">
            Warm-up has not been started for this sender. Sending uses the default limit until
            warm-up is started.
          </p>
          {isAdmin ? (
            <StartWarmupForm
              senderId={senderId}
              currentDailyLimit={data.dailyLimit}
              onDone={onClose}
            />
          ) : (
            <Alert>Only an administrator can start warm-up.</Alert>
          )}
        </>
      ) : null}
      {data && data.active ? (
        <ActiveWarmupPanel senderId={senderId} warmup={data} health={health} isAdmin={isAdmin} />
      ) : null}
    </SheetBody>
  );
}

/**
 * Phase 4 warm-up panel for a single sender — right-hand side sheet (480px),
 * never full screen. Shows plan vs. actual reached step and bounce/complaint
 * rates, and lets an administrator start (with step-up TOTP, like other
 * limit-increasing actions), pause or reset warm-up.
 */
export function WarmupSheet({
  senderId,
  senderLabel,
  isAdmin,
  open,
  onOpenChange,
}: {
  senderId: string;
  senderLabel: string;
  isAdmin: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {open ? (
        <SheetContent
          eyebrow="Warm-up"
          title={senderLabel}
          description="Gradual step-up of the daily send limit, until the entire eligible list can be reached."
        >
          <WarmupSheetBody
            senderId={senderId}
            isAdmin={isAdmin}
            onClose={() => onOpenChange(false)}
          />
        </SheetContent>
      ) : null}
    </Sheet>
  );
}
